import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { api, type AgentSummary } from "../lib/api";
import { wabaApi, type ChannelBindings, type ChannelType, type Connection } from "../lib/wabaApi";
import { Badge, inp, toast } from "./ui";

// Canais da conta (um por WhatsApp / Instagram / Messenger conectado em cada
// conexão) com o agente que atende cada um. Usado na aba Conexões (todos os
// agentes no select) e dentro do agente (só "usar neste agente").

export interface Channel { conn: Connection; type: ChannelType; label: string; ident: string; agentId: string | null }

const CH: Record<ChannelType, { label: string; cor: string }> = {
  waba: { label: "WhatsApp", cor: "#25D366" }, instagram: { label: "Instagram", cor: "#E1306C" }, messenger: { label: "Messenger", cor: "#0084FF" },
};

export function useChannels() {
  const conns = useQuery<Connection[]>({ queryKey: ["waba-conns"], queryFn: () => wabaApi.get("/connections") });
  const binds = useQueries({
    queries: (conns.data ?? []).map((c) => ({ queryKey: ["waba-binds", c.id], queryFn: () => wabaApi.get<ChannelBindings>(`/bindings/channels/${c.id}`) })),
  });
  const channels: Channel[] = [];
  (conns.data ?? []).forEach((c, i) => {
    const b = binds[i]?.data;
    if (c.phone_number_id) channels.push({ conn: c, type: "waba", label: CH.waba.label, ident: c.display_name || c.phone_number || c.name, agentId: b?.waba ?? null });
    if (c.ig_user_id) channels.push({ conn: c, type: "instagram", label: CH.instagram.label, ident: c.ig_username ? `@${c.ig_username}` : c.name, agentId: b?.instagram ?? null });
    if (c.messenger_active) channels.push({ conn: c, type: "messenger", label: CH.messenger.label, ident: c.page_name || c.name, agentId: b?.messenger ?? null });
  });
  return { channels, isLoading: conns.isLoading, isSuccess: conns.isSuccess, bindsLoading: binds.some((b) => b.isPending), error: conns.error, conns: conns.data ?? [] };
}

export function useBind() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { connection_id: string; channel: ChannelType; agent_id: string | null; agent_name?: string | null }) => wabaApi.put("/bindings/channel", p),
    onSuccess: async () => { await Promise.all([qc.invalidateQueries({ queryKey: ["waba-binds"] }), qc.invalidateQueries({ queryKey: ["waba-conns"] })]); toast("Vínculo atualizado"); },
    onError: (e: any) => toast(e?.payload?.detail ?? e?.message ?? "Erro ao vincular"),
  });
}

export function ChannelAvatar({ type }: { type: ChannelType }) {
  return <div style={{ width: 36, height: 36, borderRadius: 9, background: CH[type].cor, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{CH[type].label[0]}</div>;
}

/** Lista de canais da conta com select de agente. `agentId` fixa a visão "dentro do agente". */
export function ChannelList({ agentId }: { agentId?: string }) {
  const { channels, isLoading, error } = useChannels();
  const { data: agents = [] } = useQuery<AgentSummary[]>({ queryKey: ["agents"], queryFn: () => api.get("/agents") });
  const bind = useBind();
  const nameOf = (id: string | null) => agents.find((a) => a.id === id)?.name ?? null;

  if (isLoading) return <div style={{ color: "var(--ink-muted)", fontSize: 13 }}>Carregando canais…</div>;
  if (error) return <div style={{ color: "var(--danger)", fontSize: 13 }}>Não foi possível carregar as conexões.</div>;
  if (!channels.length) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--ink-muted)", border: "1px dashed var(--line-strong)", borderRadius: 10, fontSize: 13 }}>
        Nenhum canal conectado ainda.<br /><br />
        <Link to="/conexoes" style={{ display: "inline-block", background: "var(--brand)", color: "#fff", padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500, textDecoration: "none" }}>Ir para Conexões</Link>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {channels.map((ch) => {
        const mine = agentId ? ch.agentId === agentId : false;
        const other = agentId && ch.agentId && ch.agentId !== agentId ? nameOf(ch.agentId) : null;
        return (
          <div key={`${ch.conn.id}:${ch.type}`} style={{ background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
            <ChannelAvatar type={ch.type} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{ch.label} <span style={{ fontWeight: 400, color: "var(--ink-muted)" }}>· {ch.ident}</span></div>
              <div style={{ fontSize: 12, color: "var(--ink-dim)" }}>{ch.conn.name}{ch.conn.meta_disconnected_at ? " · desconectado na Meta" : ""}</div>
            </div>
            {ch.agentId ? <Badge tone="ok">{nameOf(ch.agentId) ?? "Em uso"}</Badge> : <Badge tone="neutral">Sem agente</Badge>}
            {agentId ? (
              <button disabled={bind.isPending} onClick={() => bind.mutate({ connection_id: ch.conn.id, channel: ch.type, agent_id: mine ? null : agentId, agent_name: mine ? null : nameOf(agentId) })}
                title={other ? `Hoje atende com ${other}; trocar solta de lá` : ""}
                style={{ height: 30, padding: "0 12px", borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: "pointer", border: `1px solid ${mine ? "var(--brand-line)" : "var(--line-strong)"}`, background: mine ? "var(--brand-soft)" : "var(--surface-card)", color: mine ? "var(--brand)" : "var(--ink-secondary)" }}>
                {mine ? "Usando neste agente" : other ? `Trocar de ${other}` : "Usar neste agente"}
              </button>
            ) : (
              <select style={{ ...inp, width: 220 }} value={ch.agentId ?? ""} disabled={bind.isPending}
                onChange={(e) => bind.mutate({ connection_id: ch.conn.id, channel: ch.type, agent_id: e.target.value || null, agent_name: nameOf(e.target.value || null) })}>
                <option value="">— sem agente —</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            )}
          </div>
        );
      })}
    </div>
  );
}
